import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import NoRecordsFound from 'components/global/NoRecordsFound';
import PageHeader from 'components/global/PageHeader';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useModalRouter from 'hooks/useModalRouter';

import { ENUMS, IMPORT } from 'services/consts';

import { getClientCards } from 'stores/userSlice';

import { DYNAMICLISTS } from 'utils/constants';

import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import ImportOrderOnboardingModal from './ImportOrdersOnboarding/ImportOrderOnboardingModal';

const ImportOrdersList = () => {
	const defaultSort = useMemo(() => [{ id: 'createdDate', desc: true }], []);

	const [isNotCRDocVerified, setIsNotCRDocVerified] = useState(false);
	const [noSavedCard, setNoSavedCard] = useState(false);

	const { generalSettings, clientCards } = useSelector((state) => state?.user);
	const dispatch = useDispatch();
	const { showModal } = useModalRouter();

	useEffect(() => {
		generalSettings?.CRDocStatus !== 'VERIFIED' ? setIsNotCRDocVerified(true) : setIsNotCRDocVerified(false);
		clientCards?.length === 0 ? setNoSavedCard(true) : setNoSavedCard(false);
	}, [generalSettings, clientCards]);

	const onboardingModalRef = useRef(false);

	const columns = useMemo(
		() => [
			{
				Header: 'ID',
				accessor: 'ID',
				filterType: FilterType.number,
				width: 70,
				show: false,
				showCopyButton: true
			},
			{
				Header: 'orderID',
				accessor: 'orderID',
				filterType: FilterType.string,
				width: 100,
				showOnMobile: true,
				showCopyButton: true
			},
			{
				Header: 'orderDate',
				accessor: 'createdDate',
				filterType: FilterType.date,
				filterRemovable: false,
				width: 100,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'grandTotal',
				accessor: 'grandTotal',
				filterType: FilterType.float,
				width: 120,
				renderCell: ListComponents.CURRENCY,
				showOnMap: true
			},
			{ Header: 'retailerURL', accessor: 'retailerURL', filterType: FilterType.string, width: 100 },
			{ Header: 'trackingURL', accessor: 'trackingURL2', filterType: FilterType.string, width: 100 },
			{ Header: 'trackingNumber', accessor: 'trackingNumber', filterType: FilterType.string, width: 100 },
			{
				Header: 'status',
				accessor: 'status',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.ALL_STATUSES,
				renderCell: ListComponents.SHIPMENT_STATUS,
				showOnMobile: true,
				width: 200,
				showOnMap: true
			},
			{
				Header: 'shipmentNumber',
				accessor: 'shipmentNumber',
				filterType: FilterType.string,
				width: 100,
				renderCell: ListComponents.TRACKING_URL
			},
			{
				Header: 'deliveryName',
				accessor: 'deliveryName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DELIVERY_COMPANIES_USED,
				width: 90,
				renderCell: ListComponents.DC_LOGO,
				show: false
			},
			{
				Header: 'originCountry',
				accessor: 'originCountry',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				width: 100
			},
			{
				Header: 'destinationCountry',
				accessor: 'warehouseCountryName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				width: 100,
				show: false
			},
			{
				Header: 'destinationCity',
				accessor: 'originCity',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'warehouseLocation',
				accessor: 'warehouseAddress',
				filterType: FilterType.string,
				width: 130,
				show: false
			},
			{ Header: 'comments', accessor: 'comments', filterType: FilterType.string, width: 120, show: false },
			{
				Header: 'brandName',
				accessor: 'storeName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.BRAND_NAME,
				width: 100,
				renderCell: ListComponents.BRAND_LOGO,
				roleName: 'BrandingModule'
			},
			{
				Header: 'paymentMethod',
				accessor: 'paymentMethod',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PAYMENT_METHOD_FOR_LIST,
				width: 100,
				showOnMap: true
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: columns.find((c) => c.accessor === 'createdDate'),
				action: FilterAction.between,
				value: {
					start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
					end: dayjs().endOf('day').toISOString()
				}
			}
		],
		[]
	);

	useEffect(() => {
		dispatch(
			getClientCards({
				isCvvRequire: false
			})
		);
	}, []);

	useEffect(() => {
		if (!onboardingModalRef.current && (isNotCRDocVerified || noSavedCard)) {
			NiceModal.show(ImportOrderOnboardingModal, {
				startIndex: isNotCRDocVerified ? 0 : 1,
				hasSavedCard: !noSavedCard
			});
			onboardingModalRef.current = true;
		}
	}, [isNotCRDocVerified, noSavedCard]);

	const AddImportOrderButton = () => (
		<Button
			icon={faPlus}
			lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
			label="addImportOrder"
			onClick={() => {
				if (isNotCRDocVerified || noSavedCard) {
					return NiceModal.show(ImportOrderOnboardingModal, {
						startIndex: isNotCRDocVerified ? 0 : 1,
						hasSavedCard: !noSavedCard
					});
				}
				return showModal('add-import-order-form');
			}}
			variant="blue"
			className="addButton"
		/>
	);

	return (
		<div>
			<PageHeader
				title="importsOrders"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/import-plane.json.gz"
				moduleName="imports">
				<Authorization path="/modal/add-import-order-form">
					<AddImportOrderButton />
				</Authorization>
			</PageHeader>

			<DynamicList
				id={DYNAMICLISTS.IMPORT_ORDERS}
				columns={columns}
				defaultFilters={defaultFilters}
				defaultSort={defaultSort}
				fetchUrl={IMPORT.GET_IMPORT_ORDERS_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.ORDER}
				exportName="importOrders"
				noRecordsFound={
					<NoRecordsFound
						lottieName="noResultsFound"
						title="noRecordsFoundTitleImportOrders"
						subTitle="noRecordsFoundSubtitleImportOrders"
						actionButton={<AddImportOrderButton />}
					/>
				}
			/>
		</div>
	);
};

export default ImportOrdersList;
